import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { ToolPageShell, StatusPill, inputCls, Field } from "@/components/AdminTools";
import MarketingAssetModal from "@/components/MarketingAssetModal";
import {
  ExternalLink,
  FileImage,
  Filter,
  Eye,
  Rocket,
  QrCode,
  Download,
  Copy,
  Mail,
  MessageSquare,
  Sparkles,
  BarChart3,
  TrendingUp,
  Activity,
  Wand2,
  RefreshCcw,
} from "lucide-react";
import { devWarn } from "@/lib/devlog";

const PROGRAM_CATS = ["all", "sleep", "metabolic", "hormone", "diagnostics", "wellness"];

export default function PortalMarketing() {
  const { user } = useAuth();
  const [tab, setTab] = useState("assets");
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [activeAsset, setActiveAsset] = useState(null);

  useEffect(() => {
    api.get("/marketing-assets")
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const downloadBrandedPdf = async (asset) => {
    try {
      const r = await api.get(`/marketing-assets/${asset.id}/render`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safe = (asset.title || "asset")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 60);
      link.download = `${safe}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Personalized PDF downloaded.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = items.filter((a) => {
    if (a.status !== "active") return false;
    if (filter === "all") return true;
    return (a.program_categories || []).includes(filter);
  });

  return (
    <ToolPageShell
      eyebrow="Clinic · Marketing"
      title="Marketing assets"
      intro="Brand-ready, vendor-coordinated templates customized with your clinic info. Preview as PDF, deploy via email, SMS, QR code, or share link."
      actions={
        <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="portal-marketing-tabs">
          <TabBtn id="assets" label="Assets" active={tab === "assets"} onClick={() => setTab("assets")} />
          <TabBtn id="qr" label="QR codes" active={tab === "qr"} onClick={() => setTab("qr")} />
          <TabBtn id="analytics" label="Analytics" active={tab === "analytics"} onClick={() => setTab("analytics")} />
        </div>
      }
    >
      {tab === "assets" && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="portal-marketing-filter">
            <span className="inline-flex items-center gap-1 text-xs text-slate-500 font-semibold mr-1">
              <Filter className="w-3 h-3" /> Filter
            </span>
            {PROGRAM_CATS.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`portal-marketing-filter-${c}`}
                onClick={() => setFilter(c)}
                className={`text-xs font-semibold capitalize rounded-full px-3 py-1.5 border ${
                  filter === c ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"
                }`}
              >
                {c === "all" ? "All" : c}
              </button>
            ))}
          </div>

          {loading && <div className="text-sm text-slate-500">Loading assets…</div>}

          {!loading && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="portal-marketing-grid">
              {filtered.map((a) => (
                <div key={a.id} data-testid={`portal-marketing-card-${a.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600">{a.kind}</div>
                      <h3 className="mt-1 font-display text-base font-semibold text-slate-900">{a.title}</h3>
                    </div>
                    <span className="w-8 h-8 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0">
                      <FileImage className="w-4 h-4 text-slate-500" />
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600 flex-1">{a.description}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(a.program_categories || []).map((c) => (
                      <span key={c} className="text-[10.5px] capitalize bg-slate-100 text-slate-700 rounded-full px-2 py-0.5">
                        {c}
                      </span>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
                    <StatusPill status={a.status} />
                    <div className="flex flex-wrap items-center gap-1.5 ml-auto">
                      <button
                        type="button"
                        onClick={() => setActiveAsset(a)}
                        data-testid={`portal-marketing-preview-${a.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-full px-2.5 py-1.5 hover:bg-slate-50 whitespace-nowrap"
                      >
                        <Eye className="w-3 h-3" /> Preview
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadBrandedPdf(a)}
                        data-testid={`portal-marketing-download-${a.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-full px-2.5 py-1.5 whitespace-nowrap"
                        title="Download a co-branded PDF — your clinic name, contact, logo and brand colors stamped in"
                      >
                        <Download className="w-3 h-3" /> Branded PDF
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveAsset(a)}
                        data-testid={`portal-marketing-deploy-${a.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-full px-2.5 py-1.5 whitespace-nowrap"
                      >
                        <Rocket className="w-3 h-3" /> Deploy
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {filtered.length === 0 && (
                <div className="sm:col-span-2 lg:col-span-3 text-center text-slate-500 py-10 rounded-2xl border border-dashed border-slate-300 bg-white">
                  No assets in this category yet.
                </div>
              )}
            </div>
          )}
        </>
      )}

      {tab === "qr" && <QrCodeStudio user={user} />}

      {tab === "analytics" && <Analytics />}

      {activeAsset && (
        <MarketingAssetModal asset={activeAsset} clinic={user} onClose={() => setActiveAsset(null)} />
      )}
    </ToolPageShell>
  );
}

function TabBtn({ id, label, active, onClick }) {
  return (
    <button
      type="button"
      data-testid={`portal-marketing-tab-${id}`}
      onClick={onClick}
      className={`text-xs font-semibold px-3.5 py-1.5 rounded-full transition-colors ${
        active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"
      }`}
    >
      {label}
    </button>
  );
}

const QR_STORAGE_KEY = "nodera_portal_qr_v1";

function QrCodeStudio({ user }) {
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [size, setSize] = useState(220);
  const [color, setColor] = useState("#0f172a");
  const [history, setHistory] = useState(() => {
    try {
      return JSON.parse(window.localStorage.getItem(QR_STORAGE_KEY) || "[]");
    } catch {
      return [];
    }
  });

  const persist = (next) => {
    setHistory(next);
    try {
      window.localStorage.setItem(QR_STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      devWarn("[PortalMarketing] QR history persist failed:", e);
    }
  };

  const save = () => {
    if (!url.trim()) {
      toast.error("Add a destination URL.");
      return;
    }
    const entry = { id: Math.random().toString(36).slice(2, 9), label: label || "Untitled QR", url: url.trim(), color, size, created_at: new Date().toISOString() };
    persist([entry, ...history].slice(0, 50));
    setLabel("");
    setUrl("");
    toast.success("QR saved.");
  };

  const remove = (id) => persist(history.filter((q) => q.id !== id));

  const downloadQr = (entry) => {
    const node = document.querySelector(`canvas[data-qr-id="${entry.id}"]`);
    if (!node) {
      toast.error("Render not ready, try again.");
      return;
    }
    const isMobile = /iPhone|iPad|iPod|Android|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || "");
    if (isMobile) {
      // The `download` attribute is silently ignored on iOS Safari + many Android
      // in-app browsers — open the PNG in a new tab so the OS share sheet can save.
      node.toBlob((blob) => {
        if (!blob) {
          toast.error("Render not ready, try again.");
          return;
        }
        const url = URL.createObjectURL(blob);
        const win = window.open(url, "_blank");
        if (!win) window.location.href = url;
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        toast.success("QR opened. Long-press the image to save it to Photos.");
      }, "image/png");
      return;
    }
    const link = document.createElement("a");
    link.download = `${(entry.label || "qr").replace(/[^a-z0-9]+/gi, "_")}.png`;
    link.href = node.toDataURL("image/png");
    link.click();
  };

  const copyUrl = async (entry) => {
    try {
      await navigator.clipboard.writeText(entry.url);
      toast.success("URL copied.");
    } catch {
      toast.error("Copy failed.");
    }
  };

  const mailto = (entry) =>
    `mailto:?subject=${encodeURIComponent((user?.clinic_name || "Clinic") + " — " + (entry.label || "Program"))}&body=${encodeURIComponent(entry.url)}`;
  const sms = (entry) => `sms:?body=${encodeURIComponent(entry.url)}`;

  return (
    <div data-testid="portal-marketing-qr-studio" className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">
          <QrCode className="w-3.5 h-3.5" /> Build a QR code
        </div>
        <p className="text-sm text-slate-600 mb-4">
          Create a custom QR for your program landing page, screener, or any URL — then deploy it across channels.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Label" hint="Internal — only you see this">
            <input
              data-testid="qr-input-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className={inputCls}
              placeholder="e.g. Sleep program landing"
            />
          </Field>
          <Field label="Destination URL" required>
            <input
              data-testid="qr-input-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className={inputCls}
              placeholder="https://yourclinic.example/sleep"
            />
          </Field>
          <Field label="Color">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 w-20 rounded-lg border border-slate-200 bg-white p-1"
            />
          </Field>
          <Field label="Size (px)">
            <input
              type="range"
              min={120}
              max={400}
              value={size}
              onChange={(e) => setSize(Number(e.target.value))}
              className="w-full"
            />
            <span className="text-xs text-slate-500 tabular">{size}px</span>
          </Field>
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-5 items-start">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 flex items-center justify-center min-h-[220px]">
            {url ? (
              <QRCodeCanvas value={url} size={Math.min(size, 320)} bgColor="#ffffff" fgColor={color} includeMargin />
            ) : (
              <p className="text-sm text-slate-500 text-center">Enter a URL to preview your QR.</p>
            )}
          </div>
          <div className="space-y-2">
            <button
              type="button"
              onClick={save}
              data-testid="qr-save-btn"
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"
            >
              <Sparkles className="w-3.5 h-3.5" /> Save to my codes
            </button>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              Saved QR codes stay in this browser. After saving, you can download as PNG, copy the URL, or share via
              email/SMS.
            </p>
          </div>
        </div>
      </div>

      <div>
        <h2 className="font-display text-lg font-bold text-slate-900 mb-3">My QR codes</h2>
        {history.length === 0 ? (
          <div className="text-center text-slate-500 py-10 rounded-2xl border border-dashed border-slate-300 bg-white">
            No QR codes yet — build one above.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="qr-list">
            {history.map((q) => (
              <div key={q.id} data-testid={`qr-item-${q.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-slate-900 truncate">{q.label}</div>
                    <code className="text-[11px] text-slate-500 break-all line-clamp-1">{q.url}</code>
                  </div>
                </div>
                <div className="mt-3 flex justify-center bg-slate-50 rounded-xl border border-slate-100 p-3">
                  <QRCodeCanvas
                    value={q.url}
                    size={140}
                    bgColor="#ffffff"
                    fgColor={q.color || "#0f172a"}
                    includeMargin
                    {...{ "data-qr-id": q.id }}
                  />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => downloadQr(q)}
                    data-testid={`qr-download-${q.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-slate-900 bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50"
                  >
                    <Download className="w-3 h-3" /> PNG
                  </button>
                  <button
                    onClick={() => copyUrl(q)}
                    data-testid={`qr-copy-${q.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-slate-900 bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50"
                  >
                    <Copy className="w-3 h-3" /> URL
                  </button>
                  <a
                    href={mailto(q)}
                    data-testid={`qr-email-${q.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-slate-900 bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50"
                  >
                    <Mail className="w-3 h-3" /> Email
                  </a>
                  <a
                    href={sms(q)}
                    data-testid={`qr-sms-${q.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-slate-900 bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50"
                  >
                    <MessageSquare className="w-3 h-3" /> SMS
                  </a>
                  <button
                    onClick={() => remove(q.id)}
                    className="inline-flex items-center text-xs text-red-600 hover:text-red-800 ml-auto"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const CHANNEL_META = {
  email: { label: "Email", icon: Mail, tone: "bg-blue-50 text-blue-700 border-blue-100" },
  sms: { label: "SMS", icon: MessageSquare, tone: "bg-amber-50 text-amber-700 border-amber-100" },
  qr: { label: "QR", icon: QrCode, tone: "bg-violet-50 text-violet-700 border-violet-100" },
  link: { label: "Share link", icon: Copy, tone: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  other: { label: "Other", icon: Activity, tone: "bg-slate-100 text-slate-700 border-slate-200" },
};

function fmtAt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/marketing-analytics")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-slate-500">Loading analytics…</div>;
  if (!data) return null;

  const channels = Object.entries(data.by_channel || {}).sort((a, b) => b[1] - a[1]);
  const totalChannelDeploys = channels.reduce((s, [, v]) => s + v, 0) || 1;

  return (
    <div className="space-y-8" data-testid="portal-marketing-analytics">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Total deploys" value={data.total_deploys || 0} icon={BarChart3} testId="analytics-stat-deploys" />
        <Stat label="Conversions" value={data.total_conversions || 0} icon={Sparkles} testId="analytics-stat-conversions" />
        <Stat
          label="Conversion rate"
          value={`${data.conversion_rate || 0}%`}
          icon={TrendingUp}
          testId="analytics-stat-rate"
        />
        <Stat
          label="Top asset"
          value={(data.top_assets || [])[0]?.asset_title?.slice(0, 22) || "—"}
          icon={FileImage}
        />
      </div>

      {/* Funnel */}
      <section data-testid="analytics-funnel">
        <h2 className="font-display text-lg font-bold text-slate-900 mb-3">Deploys → Conversions funnel</h2>
        <FunnelBar deploys={data.total_deploys || 0} conversions={data.total_conversions || 0} />
        <p className="mt-2 text-[11px] text-slate-500">
          A conversion = a screener submission on a deployment that was linked to a deployed marketing asset.
        </p>
      </section>

      {/* AI suggestions */}
      <AiSuggestions hasData={(data.total_deploys || 0) > 0} />

      {/* By channel */}
      <section>
        <div className="flex items-end justify-between mb-3">
          <h2 className="font-display text-lg font-bold text-slate-900">By channel</h2>
          <span className="text-xs text-slate-500">{totalChannelDeploys} deploys logged</span>
        </div>
        {channels.length === 0 ? (
          <div className="text-center text-slate-500 py-8 rounded-2xl border border-dashed border-slate-300 bg-white">
            No deploys logged yet. Use the Deploy button on any asset to start tracking.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="analytics-channels">
            {channels.map(([id, count]) => {
              const meta = CHANNEL_META[id] || CHANNEL_META.other;
              const Icon = meta.icon;
              const pct = Math.round((count / totalChannelDeploys) * 100);
              return (
                <li
                  key={id}
                  data-testid={`analytics-channel-${id}`}
                  className="rounded-2xl border border-slate-200 bg-white p-4"
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center border ${meta.tone}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    <div className="font-display font-semibold text-slate-900 capitalize">{meta.label}</div>
                    <div className="ml-auto flex items-center gap-3">
                      <div className="font-display text-base font-bold text-slate-900 tabular">{count}</div>
                      <div className="text-xs text-slate-500 tabular w-12 text-right">{pct}%</div>
                    </div>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-slate-900" style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Top assets */}
      <section>
        <h2 className="font-display text-lg font-bold text-slate-900 mb-3">Top-performing assets</h2>
        {(data.top_assets || []).length === 0 ? (
          <div className="text-center text-slate-500 py-8 rounded-2xl border border-dashed border-slate-300 bg-white">
            Once you deploy assets, top performers show up here.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="analytics-top-assets">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Asset</th>
                  <th className="text-left px-4 py-3">Deploys</th>
                  <th className="text-left px-4 py-3">Conversions</th>
                  <th className="text-left px-4 py-3">Conv. rate</th>
                  <th className="text-left px-4 py-3">Last deployed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.top_assets.map((a) => (
                  <tr key={a.asset_id} data-testid={`analytics-asset-${a.asset_id}`}>
                    <td className="px-4 py-3 font-display font-semibold text-slate-900">{a.asset_title}</td>
                    <td className="px-4 py-3 tabular">{a.deploys}</td>
                    <td className="px-4 py-3 tabular text-emerald-700 font-semibold">{a.conversions ?? 0}</td>
                    <td className="px-4 py-3 tabular text-slate-700">{a.conversion_rate ?? 0}%</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{fmtAt(a.last_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Recent activity */}
      <section>
        <h2 className="font-display text-lg font-bold text-slate-900 mb-3">Recent activity</h2>
        {(data.recent || []).length === 0 ? (
          <div className="text-center text-slate-500 py-8 rounded-2xl border border-dashed border-slate-300 bg-white">
            No recent deploys yet.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="analytics-recent">
            {data.recent.map((e) => {
              const meta = CHANNEL_META[e.channel] || CHANNEL_META.other;
              const Icon = meta.icon;
              return (
                <li
                  key={e.id}
                  data-testid={`analytics-recent-${e.id}`}
                  className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-3"
                >
                  <span className={`w-7 h-7 rounded-md flex items-center justify-center border ${meta.tone}`}>
                    <Icon className="w-3 h-3" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold text-slate-900 text-sm truncate">{e.asset_title}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {meta.label}
                      {e.target ? ` → ${e.target}` : ""}
                      {e.note ? ` · ${e.note}` : ""}
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-400 whitespace-nowrap">{fmtAt(e.created_at)}</div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <p className="text-[11px] text-slate-400">
        Email + SMS deploys are currently <span className="font-semibold text-slate-600">LOG-ONLY</span> until ops
        activates Resend / Twilio. Counts above include all logged attempts regardless of activation status.
      </p>
    </div>
  );
}

function Stat({ label, value, icon: Icon, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3">
      <span className="w-9 h-9 rounded-lg bg-slate-50 border border-slate-200 text-slate-600 flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </span>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 truncate">{label}</div>
        <div className="font-display text-lg font-bold text-slate-900 tabular truncate">{value}</div>
      </div>
    </div>
  );
}

function FunnelBar({ deploys, conversions }) {
  const pct = deploys ? Math.min(100, Math.round((conversions / deploys) * 100)) : 0;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="grid grid-cols-2 gap-4 text-sm mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">Deploys</div>
          <div className="font-display text-2xl font-bold text-slate-900 tabular">{deploys}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">Conversions</div>
          <div className="font-display text-2xl font-bold text-emerald-700 tabular">{conversions}</div>
        </div>
      </div>
      <div className="relative h-3 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-slate-900" style={{ width: "100%" }} />
        <div className="absolute inset-y-0 left-0 bg-emerald-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
        <span>0%</span>
        <span className="font-semibold text-slate-900">Conv rate · {pct}%</span>
        <span>100%</span>
      </div>
    </div>
  );
}



function AiSuggestions({ hasData }) {
  const [suggestions, setSuggestions] = useState([]);
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.post("/marketing-ai-suggestions");
      setSuggestions(r.data?.suggestions || []);
      setModel(r.data?.model || null);
      setHasRun(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="analytics-ai-suggestions" className="rounded-2xl border border-slate-900 bg-slate-900 text-white p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-300 inline-flex items-center gap-1.5">
            <Wand2 className="w-3.5 h-3.5" /> AI coaching · weekly
          </div>
          <h2 className="font-display text-xl font-bold mt-1">What should I do next?</h2>
          <p className="mt-1 text-sm text-slate-300 max-w-xl">
            Claude reviews your analytics and surfaces 2–3 plain-English recommendations grounded in your real numbers.
          </p>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={loading || !hasData}
          data-testid="ai-suggestions-run-btn"
          className="inline-flex items-center gap-1.5 bg-white text-slate-900 rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-100 disabled:opacity-50 shrink-0"
        >
          {loading ? (
            <>
              <RefreshCcw className="w-3.5 h-3.5 animate-spin" /> Thinking…
            </>
          ) : (
            <>
              <Sparkles className="w-3.5 h-3.5" /> {hasRun ? "Refresh" : "Generate"}
            </>
          )}
        </button>
      </div>

      {!hasData && (
        <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4 text-sm text-slate-300">
          Deploy at least one marketing asset to unlock AI coaching.
        </div>
      )}

      {hasData && !hasRun && !loading && (
        <div className="rounded-xl border border-dashed border-slate-700 bg-slate-800/40 p-4 text-sm text-slate-300">
          Click <strong className="text-white">Generate</strong> to get 2–3 actionable recommendations based on your
          live data.
        </div>
      )}

      {suggestions.length > 0 && (
        <ul className="space-y-3" data-testid="ai-suggestions-list">
          {suggestions.map((s, i) => (
            <li
              key={i}
              data-testid={`ai-suggestion-${i}`}
              className="rounded-xl border border-slate-700 bg-slate-800/60 p-4"
            >
              <div className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-md bg-blue-500/15 border border-blue-400/30 text-blue-200 flex items-center justify-center shrink-0 font-display font-bold text-sm tabular">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold text-white">{s.title}</div>
                  <p className="mt-1 text-sm text-slate-300 leading-relaxed">{s.body}</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {hasRun && model && (
        <p className="mt-3 text-[11px] text-slate-500">Model: {model}</p>
      )}
    </section>
  );
}
